import React from "react";
import "./css/NationalCodex.css";
import pictureOne from "./assets/picOne.png";
import { CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

const functionsList = [
  "Disseminating Codex Alimentarius standards and guidelines to stakeholders.",
  "Providing advice on food safety, quality, and labeling requirements.",
  "Coordinating Malawi’s participation in Codex-related international meetings.",
  "Supporting compliance with national and international food regulations.",
  "Promoting safe and quality food production and trade practices.",
];

const benefitsList = [
  "Improves food safety and quality in Malawi.",
  "Supports compliance with international food trade standards.",
  "Enhances consumer protection and confidence in food products.",
  "Facilitates trade and reduces barriers for food exports.",
  "Promotes stakeholder awareness of Codex standards and guidelines.",
];

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
    },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const NationalCodex = () => {
  return (
    <div className="national-codex-page">
      <section className="codex-header full-width-section">
        <div className="overlay">
          <h1 className="page-title">National Codex</h1>
          <p className="subtitle">
            Ensuring food safety, quality, and compliance through international
            standards.
          </p>
        </div>
      </section>
      <section className="intro-section">
        <motion.div
          className="intro-card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={slideLeft}
        >
          <h2>About National Codex</h2>
          <p>
            The Malawi Bureau of Standards (MBS) serves as the National Codex
            Contact Point, facilitating the implementation of the Codex
            Alimentarius Commission standards. This ensures that food products
            in Malawi meet internationally recognized safety, quality, and
            labeling requirements.
          </p>
        </motion.div>

        <motion.div
          className="intro-card"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={slideRight}
        >
          <h2>Our Role</h2>
          <p>
            MBS coordinates the adoption and dissemination of Codex standards,
            provides guidance to stakeholders, and supports compliance
            monitoring for food safety, labeling, and hygiene, promoting safe
            food trade nationally and internationally.
          </p>
        </motion.div>
      </section>
      <section className="functions-section full-width-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Functions of the National Codex Contact Point
          </motion.h2>

          <div className="functions-grid">
            {functionsList.map((func, idx) => (
              <motion.div
                key={idx}
                className="function-card"
                custom={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <CheckCircle color="#FF5400" size={24} />
                <p>{func}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="background-section full-width-section">
        <div className="container bg-grid">
          <motion.div
            className="text-content"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideLeft}
          >
            <h2>Background</h2>
            <p>
              The Codex Alimentarius Commission develops internationally
              recognized food standards, codes of practice, and guidelines. MBS,
              as Malawi’s Codex Contact Point, ensures that these standards are
              implemented to protect consumers and facilitate international
              trade.
            </p>

            <h2>Mandate</h2>
            <p>
              To coordinate, implement, and monitor Codex Alimentarius standards
              in Malawi, provide guidance to food industry stakeholders, and
              ensure compliance with national and international food safety and
              quality requirements.
            </p>
          </motion.div>

          <motion.div
            className="image-content"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img src={pictureOne} alt="National Codex Illustration" />
          </motion.div>
        </div>
      </section>
      <section className="benefits-section full-width-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Benefits of the National Codex Contact Point
          </motion.h2>

          <ul className="benefits-list">
            {benefitsList.map((benefit, idx) => (
              <motion.li
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2, duration: 0.5 }}
              >
                <CheckCircle color="#FF5400" size={20} />
                {benefit}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default NationalCodex;
